import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { apiurl } from '../../utils/constants';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import Paginator from '../paginator';

// Gráfico circular de órdenes por estado: Pendientes (stat='O') y Cerradas (stat='C').
function OrdeByState({ orders, selectedStat, onSelectStat }) {

	const counts = useMemo(() => {
		const next = { O: 0, C: 0 };
		orders.forEach(o => { if (o.stat === 'O' || o.stat === 'C') next[o.stat]++; });
		return next;
	}, [orders]);

	const options = useMemo(() => ({
		chart: { type: 'pie', height: 340 },
		title: { text: 'Estado de órdenes' },
		credits: { enabled: false },
		series: [{
			name: 'Estados',
			data: [
				{ name: 'Pendiente', y: counts.O, color: '#09F', stat: 'O', sliced: selectedStat === 'O', selected: selectedStat === 'O' },
				{ name: 'Cerrada', y: counts.C, color: '#666', stat: 'C', sliced: selectedStat === 'C', selected: selectedStat === 'C' }
			],
			size: '100%'
		}],
		tooltip: {
			headerFormat: '',
			pointFormat: '<b>{point.name}</b>: {point.y}<br/>'
		},
		plotOptions: {
			pie: {
				cursor: 'pointer',
				dataLabels: {
					enabled: true,
					format: '{point.name}: {point.y}',
					distance: -50,
					allowOverlap: true,
					style: {
						color: 'white',
						textOutline: 'none',
						textDecoration: 'none'
					}
				},
				showInLegend: false,
				point: {
					events: {
						click: function () {
							onSelectStat(selectedStat === this.options.stat ? '' : this.options.stat);
						}
					}
				}
			}
		}
	}), [counts, selectedStat, onSelectStat]);

	return <HighchartsReact highcharts={Highcharts} options={options} />;
}

// Gráfico de cambios de estado por mes (columnas apiladas), igual que en listStat
// pero con solo dos estados: Emitida (orde.sent) y Cerrada (orde.done).
function OrdeStateChangesByMonth({ orders, from = '', to = '' }) {

	const STATE_COLORS = { Emitida: '#09F', Cerrada: '#666' };
	const STATES = ['Emitida', 'Cerrada'];
	const MONTHS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

	const options = useMemo(() => {
		const buckets = {};
		const add = (dateStr, state) => {
			if (!dateStr) return;
			// Solo cuenta la acción si ocurrió dentro del rango filtrado
			const day = dateStr.slice(0, 10);
			if ((from && day < from) || (to && day > to)) return;
			const k = day.slice(0, 7);
			if (!buckets[k]) buckets[k] = { Emitida: 0, Cerrada: 0 };
			buckets[k][state]++;
		};

		orders.forEach(o => {
			add(o.sent, 'Emitida');
			// 'done' es la última fecha de actualización en SAP, no una fecha de cierre dedicada:
			// se refresca en cada ciclo del cron aunque la orden siga abierta. Solo cuenta como
			// "Cerrada" si además stat==='C'.
			if (o.stat === 'C') add(o.done, 'Cerrada');
		});

		const keys = Object.keys(buckets).sort();
		const sameYear = new Set(keys.map(k => k.split('-')[0])).size <= 1;
		const categories = keys.map(k => {
			const [y, m] = k.split('-');
			return sameYear ? MONTHS[Number(m) - 1] : `${MONTHS[Number(m) - 1]} ${y.slice(2)}`;
		});

		return {
			chart: { type: 'column', height: 340 },
			title: { text: 'Cambios de estado por mes' },
			credits: { enabled: false },
			xAxis: { categories },
			yAxis: { min: 0, title: { text: 'Órdenes' } },
			legend: { reversed: true },
			plotOptions: { column: { stacking: 'normal' } },
			tooltip: {
				headerFormat: '<b>{point.x}</b><br/>',
				pointFormat: '{series.name}: {point.y}<br/>Total: {point.stackTotal}'
			},
			series: STATES.map(st => ({
				name: st,
				color: STATE_COLORS[st],
				data: keys.map(k => buckets[k][st]),
			})),
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [orders, from, to]);

	return <HighchartsReact highcharts={Highcharts} options={options} />;
}

// Tiempo entre estados: como solo hay 2 estados, el único cambio es done - sent (cierre - emisión).
// A diferencia de TimeBetweenStates en listStat (promedio por cada cambio), acá el eje X son
// rangos de tiempo (histograma) y no una categoría de cambio de estado.
function OrdeTimeBetweenStates({ orders }) {

	// 'sent' y 'done' llegan siempre a medianoche (sin hora real), por lo que la diferencia
	// entre ambos es siempre múltiplo de 24h: los rangos se definen en días, no en horas.
	const BUCKETS = [
		{ label: '0', maxDays: 0.5 },
		{ label: '1-2', maxDays: 2.5 },
		{ label: '3-7', maxDays: 7.5 },
		{ label: '8-15', maxDays: 15.5 },
		{ label: '16-30', maxDays: 30.5 },
		{ label: '>30', maxDays: Infinity },
	];

	const options = useMemo(() => {
		const counts = BUCKETS.map(() => 0);

		orders.forEach(o => {
			if (o.stat !== 'C' || !o.sent || !o.done) return;
			const diffDays = (new Date(o.done) - new Date(o.sent)) / 86400000;
			if (isNaN(diffDays) || diffDays < 0) return;
			const idx = BUCKETS.findIndex(b => diffDays < b.maxDays);
			counts[idx]++;
		});

		return {
			chart: { type: 'column', height: 340 },
			title: { text: 'Tiempo entre estados' },
			credits: { enabled: false },
			xAxis: { categories: BUCKETS.map(b => b.label), title: { text: 'Días' }, labels: { rotation: 0 } },
			yAxis: { min: 0, title: { text: 'Órdenes' } },
			legend: { enabled: false },
			tooltip: { headerFormat: '<b>Cierre en {point.key} días</b><br/>', pointFormat: '{point.y} órdenes' },
			plotOptions: { column: { colorByPoint: true } },
			colors: ['#0A2', '#8BC34A', '#FC0', '#F90', '#F60', '#C00'],
			series: [{ name: 'Órdenes', data: counts }],
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [orders]);

	return <HighchartsReact highcharts={Highcharts} options={options} />;
}

// Tabla de órdenes: código de orden, RUT y fecha. Sin encabezado, igual que en listStat.
function OrdesTable({ orders }) {

	const [paginatedOrdes, setPaginatedOrdes] = useState([]);
	const [currentPage, setCurrentPage] = useState(1);
	const itemsPerPage = 10;

	useEffect(() => {
		setCurrentPage(1);
	}, [orders]);

	useEffect(() => {
		const startIndex = (currentPage - 1) * itemsPerPage;
		const endIndex = startIndex + itemsPerPage;
		setPaginatedOrdes(orders.slice(startIndex, endIndex));
	}, [orders, currentPage]);

	return (
		<>
			<h2>
				<div>Órdenes</div>
			</h2>
			<table>
				<tbody>
					{
						paginatedOrdes.map((orde, index) => (
							<tr key={index}>
								<td>{orde.docu}</td>
								<td>{orde.prov}</td>
								<td>{orde.sent ? orde.sent.slice(0, 10).split('-').reverse().join('/') : '-'}</td>
							</tr>
						))
					}
				</tbody>
			</table>
			<Paginator totalItems={orders.length} itemsPerPage={itemsPerPage} currentPage={currentPage} onPageChange={setCurrentPage} />
		</>
	);
}

function StatOrde({ onMenuItemClick }) {

	// TODO: falta definir de dónde se obtienen los datos para los demás gráficos
	const { mail } = useSelector((state) => state.user);
	const [orders, setOrders] = useState([]);
	const [selectedStat, setSelectedStat] = useState('');
	const now = new Date();
	const firstDayOfMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
	const [filtSentStart, setFiltSentStart] = useState(firstDayOfMonth);
	const [filtSentEnd, setFiltSentEnd] = useState('');
	const [selectedCent, setSelectedCent] = useState(false);

	const loadOrders = useCallback(async () => {
		try {
			const response = await fetch(apiurl + 'queryAux', {
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					user: mail,
					query: `SELECT orde.docu, orde.prov, orde.sent, orde.done, orde.stat, soli.cent, cent.name cc_name
								FROM ord__form orde
								LEFT JOIN req__soli soli ON soli.entry = orde.entr
								LEFT JOIN req__cent cent ON cent.code = soli.cent
								ORDER BY orde.sent DESC;`
				})
			});

			if (response.ok) {
				const data = await response.json();
				setOrders(Array.isArray(data) ? data : []);
			} else {
				console.error('Error al obtener las órdenes');
			}
		} catch (error) {
			console.error('Error en loadOrders:', error);
		}
	}, [mail]);

	useEffect(() => {
		loadOrders();
	}, [loadOrders]);

	// Funciones de filtro componibles: cada una aplica un solo criterio y se pueden encadenar,
	// ej. applyFiltStat(applyFiltCC(applyFiltFecha(orders))).
	const applyFiltFecha = useCallback((data) => data.filter(o =>
		(!filtSentStart || (o.sent && o.sent.slice(0, 10) >= filtSentStart))
		&& (!filtSentEnd || (o.sent && o.sent.slice(0, 10) <= filtSentEnd))
	), [filtSentStart, filtSentEnd]);

	const applyFiltCC = useCallback((data) => (
		!selectedCent ? data : data.filter(o => o.cc_name === selectedCent)
	), [selectedCent]);

	const applyFiltStat = useCallback((data) => (
		!selectedStat ? data : data.filter(o => o.stat === selectedStat)
	), [selectedStat]);

	// Base filtrada solo por fecha: para gráficos que necesitan bucketear varios campos de fecha
	// por su cuenta (ej. Emitida/Cerrada por mes), donde filtrar antes por 'sent' dejaría fuera
	// órdenes cuya 'done' cae en el rango pero cuyo 'sent' no. Se usa sin pasar por applyFiltFecha.
	const ccOnlyOrders = useMemo(() => applyFiltCC(orders), [orders, applyFiltCC]);
	const fechaFilteredOrders = useMemo(() => applyFiltFecha(orders), [orders, applyFiltFecha]);

	// Centros de costo: total de órdenes y abiertas, igual que en listStat (allí era total/abiertas por solicitud).
	const centStats = useMemo(() => {
		const buckets = {};
		fechaFilteredOrders.forEach(o => {
			if (!o.cc_name) return;
			if (!buckets[o.cc_name]) buckets[o.cc_name] = { total: 0, abiertas: 0 };
			buckets[o.cc_name].total++;
			if (o.stat === 'O') buckets[o.cc_name].abiertas++;
		});
		return Object.entries(buckets)
			.map(([cent_name, v]) => ({ cent_name, ...v, porcentaje: v.abiertas / v.total }))
			.sort((a, b) => b.porcentaje - a.porcentaje);
	}, [fechaFilteredOrders]);

	// Fecha + CC: usado por el gráfico circular (no se filtra a sí mismo por estado).
	const centFilteredOrders = useMemo(() => applyFiltCC(fechaFilteredOrders), [fechaFilteredOrders, applyFiltCC]);

	// Fecha + CC + estado: usado por la tabla y cualquier otro gráfico que no maneje sus propias fechas.
	const statFilteredOrders = useMemo(() => applyFiltStat(centFilteredOrders), [centFilteredOrders, applyFiltStat]);

	return (
		<>
			<style>{
				`.highcharts-credits{
					display: none !important;
				}
			`}</style>
			<form>
			<main>
				<h1 data-icon=''>Estadísticas de Órdenes</h1>
				<nav>
					<section>
						<article>
							<OrdeByState orders={centFilteredOrders} selectedStat={selectedStat} onSelectStat={setSelectedStat} />
						</article>
					</section>
					<section>
						<article>
							<OrdeStateChangesByMonth orders={ccOnlyOrders} from={filtSentStart} to={filtSentEnd} />
						</article>
					</section>
					<section>
						<article>
							<OrdeTimeBetweenStates orders={centFilteredOrders} />
						</article>
					</section>
				</nav>
				<nav>
					<section className="wide">
						<article>
							<OrdesTable orders={statFilteredOrders} />
						</article>
					</section>
				</nav>
			</main>
			<aside>
				<article>
					<h2>Acciones </h2>
					<fieldset>
						<input type="button" value="Volver a Órdenes" onClick={() => onMenuItemClick('listOrde')} />
					</fieldset>
				</article>
				<article>
					<h2>Filtros </h2>
					<fieldset>
						<legend>Emisión</legend>
						<input type="date" value={filtSentStart} onChange={(e) => setFiltSentStart(e.target.value)} />
						<input type="date" value={filtSentEnd} onChange={(e) => setFiltSentEnd(e.target.value)} />
					</fieldset>
				</article>
				<article>
					<h2>Centro de costos</h2>
					<fieldset>
						{
							centStats.map((c) => (
								<progress
									key={c.cent_name}
									className={selectedCent === c.cent_name ? 'sel' : ''}
									value={c.abiertas}
									max={c.total}
									data-label={c.cent_name}
									data-value={`${c.abiertas}/${c.total}`}
									onClick={() => { setSelectedCent(prev => prev === c.cent_name ? false : c.cent_name); }}
								></progress>
							))
						}
					</fieldset>
				</article>
			</aside>
			</form>
		</>
	);
}

export default StatOrde;
